"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { MessageCircleQuestionMark } from "lucide-react";
const faqs = [{
  question: "How long does a typical home inspection take?",
  answer: "A standard home inspection for a single-family home usually takes between 2 to 4 hours. This timeframe can vary based on the size of the property, its age, and the complexity of the systems being evaluated. We take our time to ensure every accessible area is thoroughly checked."
}, {
  question: "What is included in the final inspection report?",
  answer: "Our comprehensive digital reports include high-resolution photos, detailed descriptions of any defects found, and clear recommendations for repairs or further evaluation. We use modern software that makes the report easy to read on any device, typically delivered within 24 hours."
}, {
  question: "Can an inspection help with my insurance premiums?",
  answer: "Yes, specific inspections like the 4-Point or Wind Mitigation are often required by carriers in South Florida to secure or renew coverage. While a clean report does not guarantee insurance coverage, specific premium rates, or policy renewals, it provides the necessary documentation your insurer needs to assess risk."
}, {
  question: "Do you check every single part of the house?",
  answer: "We inspect all visible and safely accessible components of the home, including the roof, structure, electrical, plumbing, and HVAC systems. However, we cannot inspect areas behind finished walls, under floor coverings, or any space that poses a safety risk to the inspector."
}, {
  question: "Should I be present during the home inspection?",
  answer: "While not mandatory, we highly recommend you attend the final portion of the inspection. This allows our inspector to walk you through the findings in person, explain the significance of certain issues, and answer any specific questions you might have about the property."
}];
export default function FrequentlyAskedQuestions() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: accordionRef,
    isVisible: isAccordionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(faq => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer
      }
    }))
  };
  return <section data-section-id="248204" id="frequently-asked-questions" className="bg-background text-foreground py-16 md:py-24 lg:py-32 overflow-hidden">
      {}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} />

      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        <div ref={headerRef} className={`flex flex-col items-center text-center mb-12 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-sm bg-secondary text-secondary-foreground">
            <MessageCircleQuestionMark className="w-4 h-4 mr-2 text-primary" />
            Expert Guidance
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground mb-4 tracking-tight">
            Common Questions
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl font-default">
            Find transparent answers to the most frequent inquiries about our 
            professional home inspection services in South Florida.
          </p>
        </div>

        <div ref={accordionRef} className={`bg-card rounded-lg p-6 md:p-8 shadow-lg border border-border transition-all duration-700 delay-200 ease-out ${isAccordionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-border">
                <AccordionTrigger className="text-left text-base md:text-lg font-semibold font-default text-foreground hover:text-primary transition-colors py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground font-default text-base leading-relaxed pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        <div className={`mt-12 flex justify-center transition-all duration-700 delay-400 ease-out ${isAccordionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left bg-muted/30 p-6 rounded-lg border border-border/50 w-full max-w-2xl justify-between">
            <div>
              <h4 className="font-semibold font-default text-foreground text-lg">
                Still have questions?
              </h4>
              <p className="text-muted-foreground text-sm mt-1 font-default">
                Our certified inspectors are ready to provide the clarity you need.
              </p>
            </div>
            <Button asChild size="lg" className="shrink-0 w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
              <Link to="/HomeInspectionServicePage">
                Contact Our Team
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}